import React, { useState, useEffect } from 'react';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  Switch,
  Typography,
  Button,
  InputLabel,
  TextField,
} from '@material-ui/core';
import StoreService from '../../../../services/Store/Store.service';
import { useStoreContext } from '../../../../config/StoreContext';
import { useSnackbar } from 'notistack';
import '../ScheduleHour/EventDetails.scss';
import { orderAheadIntervalTimes } from '../../../Events/EventsConfig';
interface TimeFormat {
  hours: string;
  minutes: string;
  period: string;
}

interface Schedule {
  open: boolean;
  timeIntervalFrom: TimeFormat;
  timeIntervalTo: TimeFormat;
  override: boolean;
}

interface ScheduleState {
  Monday: Schedule;
  Tuesday: Schedule;
  Wednesday: Schedule;
  Thursday: Schedule;
  Friday: Schedule;
  Saturday: Schedule;
  Sunday: Schedule;
}
interface orderAheadSettingState {
  advanceOrderWindow: number;
  advanceOrderInterval: number;
  maxOrdersPerInterval: number;
}
const ScheduleHour: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { store, setStore, fetchStore } = useStoreContext();
  const initialSchedule: Schedule = {
    open: false,
    override: false,
    timeIntervalFrom: {
      hours: '12',
      minutes: '00',
      period: 'AM',
    },
    timeIntervalTo: {
      hours: '12',
      minutes: '00',
      period: 'AM',
    },
  };
  const initialOrdeAheadSetting: orderAheadSettingState = {
    advanceOrderWindow: 0,
    advanceOrderInterval: 900,
    maxOrdersPerInterval: 100,
  };

  const [schedules, setSchedules] = useState<ScheduleState>({
    Monday: { ...initialSchedule },
    Tuesday: { ...initialSchedule },
    Wednesday: { ...initialSchedule },
    Thursday: { ...initialSchedule },
    Friday: { ...initialSchedule },
    Saturday: { ...initialSchedule },
    Sunday: { ...initialSchedule },
  });
  const [orderAheadSetting, setOrderAheadSetting] = useState<
    orderAheadSettingState
  >({ ...initialOrdeAheadSetting });

  useEffect(() => {
    if (store && store?.scheduleHour?.length > 0) {
      const updatedSchedules: ScheduleState = store.scheduleHour.reduce(
        (acc: any, daySchedule: any) => {
          const { day, timeInterval, close, override } = daySchedule;

          let timeIntervalFrom = {
            hours: '12',
            minutes: '00',
            period: 'AM',
          };
          let timeIntervalTo = {
            hours: '12',
            minutes: '00',
            period: 'AM',
          };

          if (timeInterval) {
            const [from, to] = timeInterval.split('-');
            timeIntervalFrom = parseTime(from);
            timeIntervalTo = parseTime(to);
          }

          acc[day] = {
            open: !close,
            timeIntervalFrom,
            timeIntervalTo,
            override,
          };

          return acc;
        },
        {},
      );
      setSchedules(updatedSchedules);
    }
    if (store) {
      store.orderAheadSetting.advanceOrderInterval = Math.floor(
        store.orderAheadSetting.advanceOrderInterval / 60,
      );
      store.orderAheadSetting.advanceOrderWindow = Math.floor(
        store.orderAheadSetting.advanceOrderWindow / 60,
      );
      setOrderAheadSetting(store.orderAheadSetting);
    }
  }, [store]);

  const parseTime = (time: string): TimeFormat => {
    const [timePart, period] = time.split(/(AM|PM)/);
    const [hours, minutes] = timePart.split(':');
    return {
      hours,
      minutes,
      period: period.trim(),
    };
  };

  const handleToggle = (day: keyof ScheduleState) => {
    setSchedules(prevSchedules => ({
      ...prevSchedules,
      [day]: {
        ...prevSchedules[day],
        open: !prevSchedules[day].open,
        override: prevSchedules[day].override,
      },
    }));
  };

  const handleChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
    day: keyof ScheduleState,
    timeType: 'from' | 'to',
    field: 'hours' | 'minutes' | 'period',
  ) => {
    const { value } = event.target;
    setSchedules(prevSchedules => ({
      ...prevSchedules,
      [day]: {
        ...prevSchedules[day],
        [timeType === 'from' ? 'timeIntervalFrom' : 'timeIntervalTo']: {
          ...prevSchedules[day][
            timeType === 'from' ? 'timeIntervalFrom' : 'timeIntervalTo'
          ],
          [field]: value,
        },
      },
    }));
  };

  const validateTime = (day: keyof ScheduleState): boolean => {
    const { timeIntervalFrom, timeIntervalTo } = schedules[day];
    const fromTime = convertTo24HourFormat(timeIntervalFrom);
    const toTime = convertTo24HourFormat(timeIntervalTo);
    return fromTime <= toTime;
  };

  const convertTo24HourFormat = (time: TimeFormat): string => {
    let hours = parseInt(time.hours, 10);
    const minutes = parseInt(time.minutes, 10);

    if (time.period === 'PM' && hours < 12) {
      hours += 12;
    } else if (time.period === 'AM' && hours === 12) {
      hours = 0;
    }
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}`;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    try {
      event.preventDefault();
      if (store && store.id) {
        const formattedSchedules = Object.keys(schedules).map(day => {
          const {
            timeIntervalFrom,
            timeIntervalTo,
            open,
            override,
          } = schedules[day as keyof ScheduleState];
          return {
            day,
            timeInterval: open
              ? `${timeIntervalFrom.hours}:${timeIntervalFrom.minutes}${timeIntervalFrom.period}-` +
                `${timeIntervalTo.hours}:${timeIntervalTo.minutes}${timeIntervalTo.period}`
              : '',
            close: !open,
            override: override,
          };
        });
        orderAheadSetting.advanceOrderWindow =
          Math.floor(orderAheadSetting.advanceOrderWindow) * 60;
        orderAheadSetting.advanceOrderInterval =
          Math.floor(orderAheadSetting.advanceOrderInterval) * 60;

        setOrderAheadSetting(orderAheadSetting);
        const { data } = await StoreService.setStoreSchedule(store?.id, {
          schedule: formattedSchedules,
          orderAheadSetting,
        });
        setStore(data);
        return enqueueSnackbar('Schedule updated successfully', {
          variant: 'success',
          autoHideDuration: 8000,
        });
      }
      enqueueSnackbar('Store not found', {
        variant: 'error',
        autoHideDuration: 8000,
      });
    } catch (err) {
      enqueueSnackbar(err?.message, {
        variant: 'error',
        autoHideDuration: 8000,
      });
    }
  };
  console.log(
    orderAheadSetting.advanceOrderInterval,
    'orderAheadSetting.advanceOrderInterval++',
  );
  return (
    <div style={{ marginTop: '40px' }}>
      <Typography variant="h6" gutterBottom>
        SCHEDULE
      </Typography>
      <form onSubmit={handleSubmit}>
        <Grid container>
          {/* First Grid */}
          <Grid item xs={12} md={9}>
            {Object.keys(schedules).map(day => (
              <Grid container alignItems="center" key={day}>
                <Grid item xs={2} style={{ marginTop: '22px' }}>
                  <Typography variant="h6">{day}</Typography>
                </Grid>
                <Grid item xs={2} style={{ marginTop: '22px' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={schedules[day as keyof ScheduleState].open}
                        onChange={() =>
                          handleToggle(day as keyof ScheduleState)
                        }
                        name={`open-${day}`}
                        color="primary"
                      />
                    }
                    label={
                      <Typography style={{ fontWeight: 'normal' }}>
                        Open
                      </Typography>
                    }
                    labelPlacement="top"
                  />
                </Grid>
                {schedules[day as keyof ScheduleState].open && (
                  <>
                    <Grid item xs={4}>
                      <Typography>From</Typography>
                      <Grid container xs={12}>
                        <Grid item xs={3}>
                          <Select
                            value={
                              schedules[day as keyof ScheduleState]
                                .timeIntervalFrom.hours
                            }
                            onChange={e =>
                              handleChange(
                                e,
                                day as keyof ScheduleState,
                                'from',
                                'hours',
                              )
                            }
                            variant="outlined"
                            style={{ width: '70px' }}
                            MenuProps={{
                              anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left',
                              },
                              transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left',
                              },
                              getContentAnchorEl: null,
                              PaperProps: {
                                style: {
                                  maxHeight: 180,
                                },
                              },
                            }}
                          >
                            {[...Array(12)].map((_, index) => (
                              <MenuItem
                                key={index + 1}
                                value={(index + 1).toString().padStart(2, '0')}
                              >
                                {(index + 1).toString().padStart(2, '0')}
                              </MenuItem>
                            ))}
                          </Select>
                        </Grid>
                        <Grid item xs={3}>
                          <Select
                            value={
                              schedules[day as keyof ScheduleState]
                                .timeIntervalFrom.minutes
                            }
                            onChange={e =>
                              handleChange(
                                e,
                                day as keyof ScheduleState,
                                'from',
                                'minutes',
                              )
                            }
                            variant="outlined"
                            style={{ width: '70px' }}
                            MenuProps={{
                              anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left',
                              },
                              transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left',
                              },
                              getContentAnchorEl: null,
                            }}
                          >
                            <MenuItem value="00">00</MenuItem>
                            <MenuItem value="15">15</MenuItem>
                            <MenuItem value="30">30</MenuItem>
                            <MenuItem value="45">45</MenuItem>
                          </Select>
                        </Grid>
                        <Grid item xs={3}>
                          <Select
                            value={
                              schedules[day as keyof ScheduleState]
                                .timeIntervalFrom.period
                            }
                            onChange={e =>
                              handleChange(
                                e,
                                day as keyof ScheduleState,
                                'from',
                                'period',
                              )
                            }
                            variant="outlined"
                            style={{ width: '71px' }}
                            MenuProps={{
                              anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left',
                              },
                              transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left',
                              },
                              getContentAnchorEl: null,
                            }}
                          >
                            <MenuItem value="AM">AM</MenuItem>
                            <MenuItem value="PM">PM</MenuItem>
                          </Select>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography>To</Typography>
                      <Grid container xs={12}>
                        <Grid item xs={3}>
                          <Select
                            value={
                              schedules[day as keyof ScheduleState]
                                .timeIntervalTo.hours
                            }
                            onChange={e =>
                              handleChange(
                                e,
                                day as keyof ScheduleState,
                                'to',
                                'hours',
                              )
                            }
                            variant="outlined"
                            style={{ width: '70px' }}
                            MenuProps={{
                              anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left',
                              },
                              transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left',
                              },
                              getContentAnchorEl: null,
                              PaperProps: {
                                style: {
                                  maxHeight: 180,
                                },
                              },
                            }}
                          >
                            {[...Array(12)].map((_, index) => (
                              <MenuItem
                                key={index + 1}
                                value={(index + 1).toString().padStart(2, '0')}
                              >
                                {(index + 1).toString().padStart(2, '0')}
                              </MenuItem>
                            ))}
                          </Select>
                        </Grid>
                        <Grid item xs={3}>
                          <Select
                            value={
                              schedules[day as keyof ScheduleState]
                                .timeIntervalTo.minutes
                            }
                            onChange={e =>
                              handleChange(
                                e,
                                day as keyof ScheduleState,
                                'to',
                                'minutes',
                              )
                            }
                            variant="outlined"
                            style={{ width: '70px' }}
                            MenuProps={{
                              anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left',
                              },
                              transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left',
                              },
                              getContentAnchorEl: null,
                            }}
                          >
                            <MenuItem value="00">00</MenuItem>
                            <MenuItem value="15">15</MenuItem>
                            <MenuItem value="30">30</MenuItem>
                            <MenuItem value="45">45</MenuItem>
                          </Select>
                        </Grid>
                        <Grid item xs={3}>
                          <Select
                            value={
                              schedules[day as keyof ScheduleState]
                                .timeIntervalTo.period
                            }
                            onChange={e =>
                              handleChange(
                                e,
                                day as keyof ScheduleState,
                                'to',
                                'period',
                              )
                            }
                            variant="outlined"
                            style={{ width: '71px' }}
                            MenuProps={{
                              anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left',
                              },
                              transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left',
                              },
                              getContentAnchorEl: null,
                            }}
                          >
                            <MenuItem value="AM">AM</MenuItem>
                            <MenuItem value="PM">PM</MenuItem>
                          </Select>
                        </Grid>
                      </Grid>
                    </Grid>
                  </>
                )}
                {!validateTime(day as keyof ScheduleState) && (
                  <Grid item xs={12}>
                    <FormControl error>
                      <FormHelperText>
                        Store End Time Should be greater than start time
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            ))}
          </Grid>
          {/* 2nd Grid */}
          <Grid item xs={12} md={3}>
            <div className="event-container">
              <div className="event-header">
                <span id="form-details-title" className="section-title" style={{marginLeft:0}}>
                  {'Order Ahead Setting'}
                </span>
              </div>
              <div
                className="event-details"
                aria-labelledby="form-dialog-title"
              >
                <div className="event-form">
                  <Grid container>
                    <Grid item xs={10} className="form-field-container">
                      <InputLabel>Order Ahead Cut-Off Time in Hours</InputLabel>
                      <TextField
                        fullWidth
                        className="event-form__field"
                        name="advanceOrderWindow"
                        type="number"
                        inputProps={{
                          min: '0',
                          step: '1',
                        }}
                        value={orderAheadSetting.advanceOrderWindow}
                        onChange={e => {
                          setOrderAheadSetting({
                            ...orderAheadSetting,
                            advanceOrderWindow: +e.target.value,
                          });
                        }}
                      />
                      <Typography className="side-text">
                        Stop Order Ahead before each time interval
                      </Typography>
                    </Grid>
                    <Grid item xs={10} className="form-field-container">
                      <InputLabel>Select Order Ahead Interval</InputLabel>
                      <Select
                        fullWidth
                        name="advanceOrderInterval"
                        className="event-form__field"
                        value={orderAheadSetting.advanceOrderInterval}
                        onChange={(e: any) => {
                          setOrderAheadSetting({
                            ...orderAheadSetting,
                            advanceOrderInterval: e.target.value,
                          });
                        }}
                      >
                        {orderAheadIntervalTimes.map((intervalTime: number) => (
                          <MenuItem
                            key={intervalTime}
                            value={intervalTime}
                          >{`${intervalTime} minutes`}</MenuItem>
                        ))}
                      </Select>

                      <Typography className="side-text">
                        Allow your customer to choose their fulfillment time
                        slot
                      </Typography>
                    </Grid>
                    <Grid item xs={10} className="form-field-container">
                      <InputLabel>
                        Maximum Number of Orders per Interval
                      </InputLabel>
                      <TextField
                        fullWidth
                        className="event-form__field"
                        name="maxOrdersPerInterval"
                        type="number"
                        inputProps={{
                          min: '0',
                          step: '1',
                        }}
                        value={orderAheadSetting.maxOrdersPerInterval}
                        onChange={e => {
                          setOrderAheadSetting({
                            ...orderAheadSetting,
                            maxOrdersPerInterval: +e.target.value,
                          });
                        }}
                      />
                      <Typography className="side-text">
                        Cap the number of orders you receive per time slot
                      </Typography>
                    </Grid>
                    <Grid item xs={10} className="form-field-container">
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        style={{float: 'right' }}
                      >
                        Save Schedule
                      </Button>
                    </Grid>
                  </Grid>
                </div>
              </div>
            </div>
          </Grid>
        </Grid>
      </form>
    </div>
  );
};

export default ScheduleHour;

